import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Typography,
  Avatar,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  ContactMail as ContactIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { employeeAPI } from '../services/api';
import '../styles/AppShell.css';

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const str = String(dateString).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  if (str.includes('/')) {
    const parts = str.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
  }
  try {
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
  } catch (e) {
    /* ignore */
  }
  return '';
};

const formatDateForBackend = (dateString) => {
  if (!dateString) return null;
  const str = String(dateString).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  if (str.includes('/')) {
    const parts = str.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
  }
  try {
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
  } catch (e) {
    /* ignore */
  }
  return null;
};

const EmployeeForm = ({ employee, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    hire_date: '',
    salary: '',
    is_active: true,
    date_of_birth: '',
    date_of_issue: '',
    address: '',
    designation: '',
  });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (employee) {
      setFormData({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        department: employee.department || '',
        position: employee.position || '',
        hire_date: formatDateForInput(employee.hire_date),
        salary: employee.salary || '',
        is_active: employee.is_active !== undefined ? employee.is_active : true,
        date_of_birth: formatDateForInput(employee.date_of_birth),
        date_of_issue: formatDateForInput(employee.date_of_issue),
        address: employee.address || '',
        designation: employee.designation || '',
      });
    }
  }, [employee]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) setPhoto(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const submitData = {
        ...formData,
        hire_date: formatDateForBackend(formData.hire_date),
        date_of_birth: formatDateForBackend(formData.date_of_birth),
        date_of_issue: formatDateForBackend(formData.date_of_issue),
      };
      if (photo) submitData.photo = photo;
      if (employee) await employeeAPI.update(employee.id, submitData);
      else await employeeAPI.create(submitData);
      onSubmit();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const departments = [
    'Engineering',
    'Marketing',
    'Sales',
    'Human Resources',
    'Finance',
    'Operations',
    'Customer Support',
    'Product Management',
    'General',
  ];

  const positions = [
    'Software Engineer',
    'Senior Software Engineer',
    'Product Manager',
    'Marketing Manager',
    'Sales Representative',
    'HR Specialist',
    'Financial Analyst',
    'Operations Manager',
    'Customer Support Specialist',
    'Security Officer',
    'Designer',
    'Data Analyst',
    'Project Manager',
  ];

  const Section = ({ icon, title, children }) => (
    <div className="form-section">
      <div className="form-section-title">
        <span className="icon-wrap">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Section icon={<UploadIcon fontSize="small" />} title="Profile photo">
        <Box display="flex" alignItems="center" gap={2.5}>
          <Avatar
            src={photo ? URL.createObjectURL(photo) : employee?.photo_url || undefined}
            sx={{
              width: 88,
              height: 88,
              border: '3px solid',
              borderColor: 'divider',
              fontWeight: 700,
            }}
          >
            {formData.first_name?.charAt(0)}
            {formData.last_name?.charAt(0)}
          </Avatar>
          <Box>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="photo-upload"
              type="file"
              onChange={handlePhotoChange}
            />
            <label htmlFor="photo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Upload photo
              </Button>
            </label>
            {photo && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                {photo.name}
              </Typography>
            )}
          </Box>
        </Box>
      </Section>

      <Section icon={<PersonIcon fontSize="small" />} title="Personal">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField required fullWidth label="First name" name="first_name" value={formData.first_name} onChange={handleChange} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField required fullWidth label="Last name" name="last_name" value={formData.last_name} onChange={handleChange} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Designation" name="designation" value={formData.designation} onChange={handleChange} disabled={loading} placeholder="e.g. Security Officer" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch checked={formData.is_active} onChange={handleChange} name="is_active" disabled={loading} color="secondary" />
              }
              label="Active employee"
            />
          </Grid>
        </Grid>
      </Section>

      <Section icon={<WorkIcon fontSize="small" />} title="Work">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select name="department" value={formData.department} onChange={handleChange} disabled={loading} label="Department">
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select name="position" value={formData.position} onChange={handleChange} disabled={loading} label="Position">
                {positions.map((pos) => (
                  <MenuItem key={pos} value={pos}>{pos}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Hire date" name="hire_date" type="date" value={formData.hire_date} onChange={handleChange} disabled={loading} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Salary" name="salary" type="number" value={formData.salary} onChange={handleChange} disabled={loading} InputProps={{ startAdornment: '₹ ' }} />
          </Grid>
        </Grid>
      </Section>

      <Section icon={<ContactIcon fontSize="small" />} title="Contact">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField required fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} disabled={loading} />
          </Grid>
        </Grid>
      </Section>

      <Section icon={<LocationIcon fontSize="small" />} title="Location & dates">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Date of birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} disabled={loading} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Date of issue" name="date_of_issue" type="date" value={formData.date_of_issue} onChange={handleChange} disabled={loading} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" name="address" multiline rows={3} value={formData.address} onChange={handleChange} disabled={loading} placeholder="Full residential address" />
          </Grid>
        </Grid>
      </Section>

      <Box sx={{ mt: 1, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : employee ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};

export default EmployeeForm;
